(ns frontend.views.date-range-filter
  "Behaviour for the Program Activity page's date-range filter: it commits the
  range as soon as one is picked."
  (:require
   [common.util  :as u]
   [shadow.graft :as graft]))

(defn- control
  "The form control named `param` — the panel names its controls for the query
  params they submit — which for the presets is their radio group."
  [^js form param]
  (.namedItem (.-elements form) (name param)))

(defn- control-value
  "The value of the control named `param` — of the checked one, for a radio group."
  [form param]
  (.-value (control form param)))

(defn- dates-in-order?
  "Whether two ISO date strings name a range that runs forwards: both are filled
  in, and the start is no later than the end. ISO dates sort as strings the way
  they do as dates, so they can be compared as they come out of the inputs."
  [start end]
  (and (seq start)
       (seq end)
       (<= (compare start end) 0)))

(defn- custom-range-selected?
  [form]
  (= (name :custom) (control-value form :date-range)))

(def ^:private focus-memo-key "date-range-filter-focus")

(defn- control-to-focus
  "The control named `param-name` that focus belongs on — the checked one, for a
  radio group."
  [^js form param-name]
  (or (.querySelector form (str "input[name=\"" param-name "\"]:checked"))
      (.querySelector form (str "input[name=\"" param-name "\"]"))))

(defn- remember-focus-on!
  "Notes the control focus belongs on, for the page this submit is about to load."
  [^js el]
  (.setItem js/sessionStorage focus-memo-key (.-name el)))

(defn- restore-focus!
  "Puts focus back on the control the previous page submitted from, so working
  through the panel with the keyboard survives the reload each change causes."
  [^js form]
  (when-let [param-name (.getItem js/sessionStorage focus-memo-key)]
    (.removeItem js/sessionStorage focus-memo-key)
    (some-> (control-to-focus form param-name)
            (.focus))))

(defn- date-input?
  [^js el]
  (= "date" (.-type el)))

(defn- committable?
  "Whether the range the panel holds can be reported on: any preset can, a custom
  range only once its two dates run in order."
  [form]
  (or (not (custom-range-selected? form))
      (dates-in-order? (control-value form :start)
                       (control-value form :end))))

(defn- commit-on-change
  "Submits the form when a preset changes, and when a date input that was edited
  loses focus. The date inputs are only submitted on losing focus so we don't
  interrupt a user typing a date. After the form is submitted we reopen it with
  focus restored, so that you can easily switch between selections. The time
  filter component is only closed if you click outside of it, tab out of it, or
  escape."
  [^js form]
  ;; containment is tested against the form rather than the whole disclosure: the
  ;; summary sits outside the panel, so focus landing there — Escape, or a
  ;; Shift+Tab off the first preset — is the work leaving, not continuing
  (let [;; where the pointer last went down, and nil while the interaction is a
        ;; keyboard one — a click on a spot that cannot hold focus reports no
        ;; relatedTarget, so this is what says whether it landed in the panel
        pointer-target (atom nil)
        edited?        (atom false)
        commit!        (fn [^js next]
                         (when (committable? form)
                           (let [landed-on (or next @pointer-target)
                                 inside?   (boolean (and landed-on (.contains form landed-on)))]
                             (set! (.-disabled (control form :panel)) (not inside?))
                             ;; a pointer picking a preset outright would only
                             ;; gain a focus ring it never asked for; an arrow
                             ;; press walking the presets, or a move between the
                             ;; date inputs, has a place to be put back
                             (when (and inside? (or (nil? @pointer-target)
                                                    (date-input? landed-on)))
                               (remember-focus-on! landed-on))
                             (.requestSubmit form))))]
    (restore-focus! form)
    (.addEventListener form "keydown"
                       (fn [^js event]
                         (reset! pointer-target nil)
                         ;; Enter cannot submit this form on its own: two date
                         ;; inputs mean HTML skips implicit submission
                         (when (and (= "Enter" (.-key event))
                                    (date-input? (.-target event)))
                           (commit! (.-target event)))))
    (.addEventListener js/document "pointerdown"
                       (fn [^js event] (reset! pointer-target (.-target event))))
    ;; a date input is followed by whether it was edited rather than by the value
    ;; it held, since focus can be given to it more than once before it is left
    (.addEventListener form "focusin"
                       (fn [^js event]
                         (when (date-input? (.-target event))
                           (reset! edited? false))))
    (.addEventListener form "input"
                       (fn [^js event]
                         (when (date-input? (.-target event))
                           (reset! edited? true))))
    (.addEventListener form "focusout"
                       (fn [^js event]
                         (when (and (date-input? (.-target event))
                                    @edited?)
                           (commit! (.-relatedTarget event)))))
    (.addEventListener form "change"
                       (fn [^js event]
                         (when-not (date-input? (.-target event))
                           (commit! (.-target event)))))
    (.addEventListener form "submit"
                       (fn [^js event]
                         (when-not (committable? form)
                           (.preventDefault event))))))

(defmethod graft/scion (u/->scion-id :frontend.views.date-range-filter/commit-on-change)
  [_params form]
  (commit-on-change form))
